import React from 'react';

export function OutboundTab({ formData, agent, activeCallSid, callStatus, callElapsed, outboundNumber, setOutboundNumber, outboundContext, setOutboundContext, outboundLoading, outboundResult, initiateOutboundCall, hangUpCall, setActiveTab }) {
  return (
    <div className="space-y-6">
      <div className="p-5 bg-gray-50 rounded-lg border border-gray-200">
        <h4 className="font-medium text-gray-800 mb-1">Outbound-Anruf</h4>
        <p className="text-sm text-gray-500 mb-4">Lassen Sie <strong>{formData?.name || 'den Agenten'}</strong> eine Nummer anrufen.</p>

        {/* No ElevenLabs Agent Warning */}
        {!formData.elevenlabs_agent_id && (
          <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl" data-testid="outbound-no-agent-warning">
            <div className="flex items-start gap-3">
              <span className="text-xl">&#x26A0;&#xFE0F;</span>
              <div>
                <p className="font-medium text-amber-800">Agent nicht verbunden</p>
                <p className="text-sm text-amber-700 mt-1">Speichern Sie den Agenten zuerst, damit er mit ElevenLabs synchronisiert wird.</p>
              </div>
            </div>
          </div>
        )}

        {/* No Twilio Phone Warning */}
        {formData.elevenlabs_agent_id && !agent?.elevenlabs_phone_id && (
          <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl" data-testid="outbound-no-phone-warning">
            <div className="flex items-start gap-3">
              <svg className="w-6 h-6 text-amber-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" /></svg>
              <div>
                <p className="font-medium text-amber-800">Keine Telefonnummer konfiguriert</p>
                <p className="text-sm text-amber-700 mt-1">Um ausgehende Anrufe zu starten, muss eine Twilio-Nummer mit diesem Agenten verbunden sein.</p>
                <button onClick={() => setActiveTab('connections')} className="mt-3 px-4 py-2 bg-amber-600 text-white text-sm font-medium rounded-lg hover:bg-amber-700 transition-colors inline-flex items-center gap-2" data-testid="outbound-go-to-phone-btn">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6" /></svg>
                  Zur Telefon-Konfiguration
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Call Form */}
        {formData.elevenlabs_agent_id && agent?.elevenlabs_phone_id && (
          <div className="space-y-4">
            {activeCallSid && (
              <div className={`p-4 rounded-xl text-center border ${callStatus === 'in_progress' ? 'bg-emerald-50 border-emerald-300' : 'bg-amber-50 border-amber-200'}`} data-testid="outbound-call-active">
                <div className={`w-16 h-16 mx-auto rounded-full flex items-center justify-center mb-3 ${callStatus === 'in_progress' ? 'bg-emerald-100' : 'bg-amber-100 animate-pulse'}`}>
                  <svg className={`w-8 h-8 ${callStatus === 'in_progress' ? 'text-emerald-600' : 'text-amber-600'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" /></svg>
                </div>
                <p className={`font-semibold ${callStatus === 'in_progress' ? 'text-emerald-700' : 'text-amber-700'}`}>
                  {(() => {
                    if (callStatus === 'ringing') return 'Klingelt...';
                    if (callStatus === 'in_progress') return 'Gespräch läuft';
                    if (callStatus === 'initiating') return 'Wird gestartet...';
                    return 'Anruf aktiv';
                  })()}
                </p>
                {callStatus === 'in_progress' && <p className="text-lg font-mono text-emerald-600 mt-1" data-testid="call-timer">{Math.floor(callElapsed / 60)}:{String(callElapsed % 60).padStart(2, '0')}</p>}
                <p className="text-sm text-gray-500 mt-1">{outboundNumber}</p>
                <button onClick={hangUpCall} className="mt-3 px-4 py-2 bg-red-500 text-white text-sm font-medium rounded-lg hover:bg-red-600 transition-colors" data-testid="hangup-call-btn">Auflegen</button>
              </div>
            )}

            {!activeCallSid && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Telefonnummer</label>
                  <input type="tel" placeholder="+49170..." value={outboundNumber} onChange={(e) => setOutboundNumber(e.target.value)} className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm" data-testid="outbound-number-input" />
                  <p className="text-xs text-gray-400 mt-1">Format: +49... oder 0170...</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Kontext / Erste Nachricht (optional)</label>
                  <textarea rows={2} placeholder="z.B. Hallo, ich rufe wegen Ihres Termins an..." value={outboundContext} onChange={(e) => setOutboundContext(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" data-testid="outbound-context-input" />
                </div>
                <button onClick={initiateOutboundCall} disabled={outboundLoading || !outboundNumber.trim()} className="w-full px-4 py-3 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors text-sm flex items-center justify-center gap-2" data-testid="start-outbound-call-btn">
                  {outboundLoading ? (<><svg className="animate-spin h-4 w-4" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/></svg>Anruf wird gestartet...</>) : '📞 Jetzt anrufen'}
                </button>
              </>
            )}

            {outboundResult && !activeCallSid && (() => {
              const isSuccess = outboundResult.finalStatus === 'completed' || (!outboundResult.finalStatus && outboundResult.success);
              const statusClass = isSuccess ? 'bg-green-50 border border-green-200 text-green-700' : 'bg-red-50 border border-red-200 text-red-700';
              let statusLabel = 'Fehler';
              if (outboundResult.finalStatus === 'completed') statusLabel = 'Anruf beendet';
              else if (outboundResult.finalStatus === 'failed') statusLabel = 'Anruf fehlgeschlagen';
              else if (outboundResult.success) statusLabel = 'Anruf gestartet';
              return (
                <div className={`p-3 rounded-lg text-sm ${statusClass}`} data-testid="outbound-result">
                  <p className="font-medium">{statusLabel}</p>
                  <p className="text-xs mt-1">{outboundResult.message || outboundResult.detail}</p>
                </div>
              );
            })()}
          </div>
        )}
      </div>

      {/* Info Box */}
      <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg text-xs text-blue-700">
        <p className="font-medium mb-1">So funktioniert Outbound</p>
        <ul className="space-y-1">
          <li>&#x2022; Legen Sie einen eigenen Agenten an, dessen Prompt speziell für ausgehende Anrufe geschrieben ist</li>
          <li>&#x2022; Die Begrüßung und das Verhalten folgen der Agenten-Konfiguration</li>
          <li>&#x2022; Jeder Anruf wird in der Anrufhistorie protokolliert und Credits werden abgezogen</li>
          <li>&#x2022; Für Massen-Anrufe nutzen Sie den Kampagnen-Manager</li>
        </ul>
      </div>
    </div>
  );
}
